import { useEffect, useRef } from 'react';
import runFlow from './flowAnimation';

// Hero "see it work" flow: an ask composer that morphs into a zooming radial
// investigation workspace (operatives, analysis islands, packets, plan, file
// tree, orchestrator log). This component renders the static scaffold once; the
// imperative engine in ./flowAnimation drives it and is torn down on unmount.
export default function MonadFlow() {
  const stageRef = useRef(null);

  useEffect(() => runFlow(stageRef.current), []);

  return (
    <div className="WSMONAD-monad-flow" id="monad-flow" data-stage="input" ref={stageRef}>
      <span id="flow" />

      {/* INPUT LAYER */}
      <div className="WSMONAD-flow-input" id="flow-input">
        <div className="WSMONAD-fi-sphere">
          <svg id="fi-sigil" viewBox="-100 -100 200 200" xmlns="http://www.w3.org/2000/svg" />
        </div>
        <div className="WSMONAD-fi-cap">See it work</div>
        <div className="WSMONAD-ask-pill">
          <span className="WSMONAD-ask-prompt">&gt;</span>
          <span className="WSMONAD-ask-field">
            <span className="WSMONAD-ask-text" id="ask-text" />
            <span className="WSMONAD-ask-cursor" id="ask-cursor" />
          </span>
        </div>
        <div className="WSMONAD-fi-controls">
          <span className="WSMONAD-fi-lever">
            <svg className="WSMONAD-lv-ic" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M2 5.5C2 4.7 2.7 4 3.5 4H6l1.2 1.4H12.5C13.3 5.4 14 6 14 6.9V11c0 .8-.7 1.5-1.5 1.5h-9C2.7 12.5 2 11.8 2 11z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
            </svg>
            Use files from your account <span className="WSMONAD-lv-ca">▾</span>
          </span>
          <span className="WSMONAD-fi-avail">
            <span className="WSMONAD-av-dot" />
            <b>82</b> databases available
          </span>
        </div>
        <div className="WSMONAD-dropzone" id="dropzone">
          <svg className="WSMONAD-dz-bigic" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M8 11V3M8 3L5 6M8 3l3 3" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 11v2h10v-2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          <div className="WSMONAD-dz-head">
            <span className="WSMONAD-dz-text" id="dz-text">Drop your own data here</span>
            <span className="WSMONAD-dz-opt">Optional</span>
          </div>
          <div className="WSMONAD-dz-chips">
            <span className="WSMONAD-dz-chip" data-file="1">aged_cohort.fastq</span>
            <span className="WSMONAD-dz-chip" data-file="2">variants.vcf</span>
          </div>
        </div>
        <button className="WSMONAD-run-btn" id="run-btn" type="button">
          Run investigation <span className="WSMONAD-arr">↗</span>
        </button>

        <div className="WSMONAD-m-cursor" id="m-cursor">
          <svg viewBox="0 0 16 16">
            <path d="M 0 0 L 0 12 L 3.6 8.6 L 5.8 13 L 7.8 12 L 5.6 7.6 L 11 7.6 Z" fill="var(--ink)" stroke="var(--paper)" strokeWidth="0.6" />
          </svg>
          <div className="WSMONAD-m-ghost">
            <span className="WSMONAD-gf">aged_cohort.fastq</span>
            <span className="WSMONAD-gf">variants.vcf</span>
          </div>
        </div>
      </div>

      {/* WORKSPACE LAYER */}
      <div className="WSMONAD-flow-work" id="flow-work">
        <div className="WSMONAD-flow-bar">
          <div className="WSMONAD-fb-left">
            <span className="WSMONAD-fb-pip" /> RUN-0492 · <b>Cardiac aging</b>
          </div>
        </div>

        <div className="WSMONAD-flow-canvas">
          <div className="WSMONAD-flow-map">
            <svg id="flow-svg" viewBox="0 0 1000 640" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
              <g id="fl-cam">
                <g id="fl-links" />
                <g id="fl-packets" />
                <g id="fl-nodes" />
              </g>
            </svg>
          </div>

          <div className="WSMONAD-panel WSMONAD-plan">
            <div className="WSMONAD-pn-h">
              <span>Plan</span>
              <span className="WSMONAD-pn-r">
                <span className="WSMONAD-cnt" id="fp-count">0 / 8</span>
                <span className="WSMONAD-pn-chev" aria-hidden="true">⌄</span>
              </span>
            </div>
            <div className="WSMONAD-fp-list" id="fp-list" />
          </div>

          <div className="WSMONAD-panel WSMONAD-dir">
            <div className="WSMONAD-pn-h">
              <span>workspace / run-0492</span>
              <span className="WSMONAD-pn-chev" aria-hidden="true">⌄</span>
            </div>
            <div className="WSMONAD-dir-meter">
              <div className="WSMONAD-dm-top">
                <span>STORAGE</span>
                <span>
                  <b id="dm-used">0.0</b> / 128 GB
                </span>
              </div>
              <div className="WSMONAD-dm-bar">
                <span id="dm-fill" />
              </div>
            </div>
            <div className="WSMONAD-fs-tree" id="fs-tree" />
          </div>

          <div className="WSMONAD-panel WSMONAD-side">
            <div className="WSMONAD-fs-head">
              <span className="WSMONAD-fs-sphere">
                <svg id="chat-sigil" viewBox="-100 -100 200 200" xmlns="http://www.w3.org/2000/svg" />
              </span>{' '}
              <span>MONAD · Orchestrator</span>
              <span className="WSMONAD-pn-chev" aria-hidden="true">⌄</span>
            </div>
            <div className="WSMONAD-fs-log" id="fs-log" />
            <div className="WSMONAD-fs-foot">
              <span className="WSMONAD-ip">&gt;</span>
              <input id="fs-steer" placeholder="Steer the run…" readOnly />
              <button className="WSMONAD-send" type="button">Send</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
